/* eslint-disable indent */
import { TopLevelSpec as VegaLiteSpec } from 'vega-lite';
import { Axis, AxisConfig } from 'vega-lite/build/src/axis';
import { MarkDef } from 'vega-lite/build/src/mark';
import { Scale } from 'vega-lite/build/src/scale';
import { UnitSpec } from 'vega-lite/build/src/spec';
import { liteBaseConfig } from '../vegaShared';

export interface IBarOptions {
  data: any[];
  xField: string;
  xSortField: string;
  color?: string;
  colorScale?: Scale;
  colorField: string;
  yField: string;
  xAxisTitle?: string;
  yAxisTitle?: string;
  xLabels?: boolean;
  yLabels?: boolean;
  yFormat?: string;
  barLabels?: boolean;
  barLabelConfig?: MarkDef<'text'>;
  width?: number | 'container';
  height?: number | 'container';
  tooltipFields?: { field: string; title: string; format?: string; type?: string }[];
}

export const genBarSpec = (options: IBarOptions): VegaLiteSpec => {
  const n = options.data.length;
  const minWidthPerLabel = 35;
  const minWidthForLabels = minWidthPerLabel * n;
  const labelExpr = `width > ${minWidthForLabels} ? datum.value : ''`;
  const xAxis = {
    labelAngle: 0,
    title: options.xAxisTitle || null,
    labels: !!options.xLabels,
    domain: !!options.xLabels,
    ticks: !!options.xLabels,
    labelExpr,
    labelLimit: minWidthPerLabel / 1,
    labelFontWeight: 700 as 700,
    labelFontSize: 11,
  };
  const yAxis = {
    title: options.yAxisTitle || null,
    labels: !!options.yLabels,
    domain: !!options.yLabels,
    ticks: !!options.yLabels,
    format: options.yFormat || '0.2s',
    labelFontWeight: 700 as 700,
    labelFontSize: 11,
  };
  const barLayer: UnitSpec = {
    mark: {
      type: 'bar',
      tooltip: true,
      cornerRadiusTopLeft: {
        signal: 'width > 800 ? 4 : 2',
      },
      cornerRadiusTopRight: {
        signal: 'width > 800 ? 4 : 2',
      },
      ...(options.color && { fill: options.color }),
    },
  };
  const noLabelTest = `width < ${minWidthForLabels} || datum.order > 0`;

  const textLayer: UnitSpec = {
    mark: {
      type: 'text',
      align: 'center',
      baseline: 'bottom',
      dy: -3,
      fontWeight: 700,
      fontSize: 12,
      ...options.barLabelConfig,
    },
    encoding: {
      text: {
        ...(options.colorScale?.domain && { order: { field: 'order', type: 'ordinal' } }),
        aggregate: 'sum',
        field: options.yField,
        type: 'quantitative',
        format: options.yFormat || '0.2s',
        condition: {
          test: noLabelTest,
          value: '',
        },
      },
    },
  };

  const tooltipEncoding = options.tooltipFields
    ? {
        tooltip: options.tooltipFields.map((field) => ({
          field: field.field,
          title: field.title,
          type: field.type || 'nominal',
          format: field.format || '',
        })),
      }
    : { tooltip: true };

  const layers: UnitSpec[] = [barLayer];
  if (options.barLabels) {
    layers.push(textLayer);
  }

  const config = {
    ...liteBaseConfig(),
  };

  const baseSpec: VegaLiteSpec = {
    $schema: 'https://vega.github.io/schema/vega-lite/v4.json',
    autosize: 'fit',
    width: options.width || 'container',
    height: options.height || 100,
    // @ts-ignore
    config,
    ...(options.colorScale?.domain && {
      transform: [
        {
          // @ts-ignore
          calculate: `indexof(['${options.colorScale.domain.join("', '")}'], datum.${options.colorField})`,
          as: 'order',
        },
      ],
    }),
    // @ts-ignore
    encoding: {
      x: { field: options.xField, type: 'nominal', axis: xAxis, sort: { field: options.xSortField, order: 'ascending', op: 'min' } },
      y: { field: options.yField, axis: yAxis, type: 'quantitative', stack: 'zero', aggregate: 'sum' },
      ...(options.colorField && { color: { field: options.colorField, legend: null, scale: options.colorScale || {} } }),
      ...(options.colorScale?.domain && { order: { field: 'order', type: 'ordinal' } }),
      ...tooltipEncoding,
    },
    layer: layers,
    data: { values: options.data },
  };
  return baseSpec;
};
